import React, { useEffect, useState, useRef } from 'react';
import i18n from '@i18next/i18n';
import { searchImages } from '@src/slide/application/service/ai/core/api/api';
import { SearchImageInfo } from '@src/slide/application/service/ai/core/api/api.interface';
import { IPictureModel } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import Button from '@tencent/dui/lib/components/Button';
import { AiReplaceImageContentLeft } from './components/dialog-left';
import { AiReplaceImageContentRight } from './components/dialog-right';
import { ReplaceImageUtils } from '../utils/replace-image-utils';
import styles from '@src/slide/application/service/smart-replace-image/replace-dialog/replace-image-dialog.less';

const MAX_PAGE = 3;
const MAX_COUNT_PER_PAGE = 9;

interface IAiReplaceImageContentProps {
    imagesToReplace: SearchImageInfo[];
    pageIndex: number;
    close: () => void;
    getPictureModelById: (slideId: string, shapeId: string) => IPictureModel | undefined;
}

interface IDisplayImageInfo {
    src: string,
    sourceUrl: string,
}

interface ICurrentImageInfo {
    slideId: string;
    shapeId: string;
    url: string;
}

export const AiReplaceImageContent = ({
    imagesToReplace,
    pageIndex,
    close,
    getPictureModelById,
}: IAiReplaceImageContentProps) => {
    const [displayImages, setDisplayImages] = useState<IDisplayImageInfo[]>([]); // 右侧图片列表
    const [currentImages, setCurrentImages] = useState<ICurrentImageInfo[]>([]); // 左侧图片列表
    const [selectedImage, setSelectedImage] = useState<{ page: number; index: number }>(); // 右侧图片选中
    const [currentIndex, setCurrentIndex] = useState<number>(0); // 左侧图片选中

    const selectedList = useRef<Record<number, { page: number; index: number } | undefined>>({});
    const displayImagesInfo = useRef<Record<number, SearchImageInfo[]>>({ [pageIndex]: imagesToReplace });
    const currentPage = useRef<number>(pageIndex);

    useEffect(() => {
        setDisplayImages(imagesToReplace.map(i => ({ src: i.thumb_url, sourceUrl: i.source_url })));
        setCurrentImages(getAllImages());
    }, []);

    // 获取文档中AI生成的图片
    const getAllImages = (): ICurrentImageInfo[] =>
        ReplaceImageUtils.getAllAiPictures().filter(({ url }) => !!url) ?? [];

    // 追加可选择的图片
    const addDisplayImages = () => {
        currentPage.current = (currentPage.current + 1) % MAX_PAGE;
        const pageIndex = currentPage.current;
        // 有缓存的时候直接使用缓存
        if (displayImagesInfo.current[pageIndex]?.length > 0) {
            const images = displayImagesInfo.current[pageIndex];
            setDisplayImages(images.map(i => ({ src: i.thumb_url, sourceUrl: i.source_url })));
            return;
        }
        // 使用接口获取图片
        setDisplayImages(Array.from({ length: MAX_COUNT_PER_PAGE }, () => ({ src: '', sourceUrl: '' })));
        // 此接口使用相同 page 的图片不会重复
        const keyword = SlideDataServices.SlideTitleManager()?.title ?? '';
        searchImages({ keyword, num: MAX_COUNT_PER_PAGE, page: pageIndex }).then((res) => {
            const images = res?.search_image.images ?? [];
            setDisplayImages(images.map(i => ({ src: i.thumb_url, sourceUrl: i.source_url })));
            displayImagesInfo.current[pageIndex] = images;
        });
    };

    // 切换当前图片
    const changeCurrentImage = (currentIndex: number) => {
        // 修改右侧待替换图片的选中态
        setSelectedImage(selectedList.current[currentIndex]);
        // 修改左侧待替换图片的状态
        setCurrentIndex(currentIndex);
    };

    // 切换待定图片
    const onSelectedImage = (index: number) => {
        setSelectedImage({ page: currentPage.current, index });
    };

    const isCurrentLastImage = () => currentImages.length === currentIndex + 1;

    return (
        <>
            <div
                style={{
                    width: '100%',
                    minHeight: 120,
                    backgroundColor: '#E9EBED',
                    borderTop: '1px solid rgba(0,0,0,.04)',
                    borderBottom: '1px solid rgba(0,0,0,.04)',
                }}
            >
                <div className={styles['dialog-content']}>
                    <AiReplaceImageContentLeft
                        image={currentImages[currentIndex]?.url}
                        currentIndex={currentIndex}
                        totalImagesCount={currentImages.length}
                        changeCurrentImage={changeCurrentImage}
                    />
                    <AiReplaceImageContentRight
                        displayImages={displayImages}
                        selectedIndex={selectedImage?.page === currentPage.current ? selectedImage.index : undefined}
                        selectImage={onSelectedImage}
                        updateDisplayImages={addDisplayImages}
                    />
                </div>
            </div>
            <div style={{ paddingRight: 24, margin: '18px 0 18px 0', alignSelf: 'end' }}>
                <Button
                    onClick={() => {
                        if (isCurrentLastImage()) {
                            close();
                            return;
                        }
                        changeCurrentImage(currentIndex + 1);
                    }}
                >
                    {isCurrentLastImage() ? i18n.t('跳过并退出') : i18n.t('跳过本图')}
                </Button>
                <Button
                    style={{ marginLeft: 16 }}
                    type={'primary'}
                    disabled={selectedImage === undefined || currentImages.length <= 0}
                    onClick={async () => {
                        if (!selectedImage) {
                            return;
                        }

                        // 找到待替换的图片 model
                        const imageModel = getPictureModelById(
                            currentImages[currentIndex].slideId,
                            currentImages[currentIndex].shapeId,
                        );
                        if (!imageModel) {
                            return;
                        }

                        const image = displayImagesInfo.current[selectedImage.page][selectedImage.index];
                        ReplaceImageUtils.replaceImagesWithSearchInfo([imageModel], image, false);

                        // 记录替换历史
                        selectedList.current[currentIndex] = selectedImage;
                        if (isCurrentLastImage()) {
                            close();
                            return;
                        }
                        changeCurrentImage(currentIndex + 1);
                    }}
                >
                    {isCurrentLastImage() ? i18n.t('替换并退出') : i18n.t('替换')}
                </Button>
            </div>
        </>
    );
};
